import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import CancelIcon from '@/assets/icons/cancel.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import DataBlock from '@/components/DataBlock/DataBlock'
import Tag from '@/components/Tag/Tag'
import TextWithLabel from '@/components/TextWithLabel/TextWithLabel'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import type { AvailabilityForm } from '@/constants/availability-form'
import { daysOfWeek } from '@/constants/days-of-week'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'
import { useTimetableSetttings } from '@/modules/timetable'
import { AvailabilityDataBlock } from '@/modules/users'
import { joinNameArray } from '@/utils/join-array'

import styles from './RoomDetailsView.module.scss'
import RoomAvailabilityModal from '../components/RoomAvailabilityModal'
import { roomTypeLabelAndColorMap } from '../constants/room-type-to-tag-color-map'
import useRemoveRoom from '../mutations/useRemoveRoom'
import useSetRoomAvailability from '../mutations/useSetRoomAvailability'
import { useRoom, type RoomDetails } from '../queries/useRoom'

const routeApi = getRouteApi('/_auth/rooms/$roomId/')

const parseAvailabilitySlotsForForm = (
  availability: RoomDetails['availability']
): AvailabilityForm =>
  daysOfWeek.reduce(
    (acc, day) => ({
      ...acc,
      [day]: availability[day].map(slot => ({
        isOptional: slot.isOptional,
        time: {
          from: slot.startTime,
          to: slot.endTime
        }
      }))
    }),
    {} as AvailabilityForm
  )

const RoomDetailsView = () => {
  const { roomId } = routeApi.useParams()
  const { data: room, refetch: refetchRoom } = useRoom(roomId)
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation('rooms')

  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)

  const openModal = () => setIsAvailabilityModalOpen(true)

  const { data: workingHours } = useTimetableSetttings()

  const { mutate: setAvailability, isPending } = useSetRoomAvailability({
    onSuccess: () => {
      toast({
        title: t('toast.availability-successfully-set'),
        variant: 'success'
      })
      setIsAvailabilityModalOpen(false)
      refetchRoom()
    },
    onError: () => {
      toast({ title: t('toast.failed-to-set-availability'), variant: 'error' })
    }
  })

  const { mutate: removeRoom, isPending: isRemoveRoomPending } = useRemoveRoom({
    onSuccess: () => {
      toast({
        title: t('toast.room-was-removed', {
          ROOM: room?.name
        }),
        variant: 'success'
      })
      navigate({
        to: '/rooms',
        search: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      })
    },
    onError: () => {
      toast({ title: t('toast.failed-to-remove-room'), variant: 'error' })
    }
  })

  if (!room) return null

  const handleSetRoomAvailability = (data: AvailabilityForm) => {
    setAvailability({
      availabilityId: room.availability.id,
      data
    })
  }

  const handleRemoveRoom = () => {
    removeRoom({ roomId: room.id })
  }

  const actions: Action[] = [
    {
      text: t('button.edit'),
      icon: <EditIcon />,
      asLink: true,
      to: '/rooms/$roomId/edit',
      params: {
        roomId
      }
    },
    {
      text: t('button.remove'),
      icon: <CancelIcon />,
      variantAction: 'danger',
      onClick: () => {
        if (!room.canBeDeleted) {
          toast({
            title: t('toast.cannot-remove-room', {
              ROOM: room.name
            }),
            description: t('toast.room-linked-to-active-lesson'),
            variant: 'error'
          })
          return
        }
        setIsRemoveModalOpen(true)
      }
    }
  ]

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('header.rooms'),
          to: '/rooms'
        },
        {
          title: t('header.room-details')
        }
      ]}
      subheader={t('header.room-details')}
      header={<RoomDetailsHeader room={room} />}
      actions={actions}
      moduleName={t('header.administration', { ns: 'common' })}
      actionsAsDropdown
    >
      <section className={styles.section}>
        <div className={styles.detailsSectionHeader}>
          <h2 className={styles.sectionHeader}>{t('header.details')}</h2>
          <Button
            variant="secondary"
            asLink
            to="/rooms/$roomId/edit"
            icon={<EditIcon />}
          >
            {t('button.edit')}
          </Button>
        </div>
        <div className={styles.detailsSectionData}>
          <div className={styles.detailsSectionDataItem}>
            <DataBlock
              header={t('header.basic-information')}
              infoArray={[
                {
                  label: t('label.capacity'),
                  value: room.capacity
                }
              ]}
            />
            <DataBlock
              header={t('header.room-preferences')}
              infoArray={[
                {
                  label: t('label.preferred-course'),
                  value: joinNameArray(room.preferredCourses)
                },
                {
                  label: t('label.preferred-class'),
                  value: joinNameArray(room.preferredClasses)
                },
                {
                  label: t('label.preferred-user'),
                  value: joinNameArray(room.preferredUsers)
                }
              ]}
            />
          </div>
          <div>
            <TextWithLabel
              label={t('label.special-equipment')}
              text={room.specialEquipment}
            />
          </div>
        </div>
      </section>
      <section className={styles.section}>
        <div className={styles.detailsSectionHeader}>
          <h2 className={styles.sectionHeader}>{t('header.availability')}</h2>
          <Button variant="secondary" icon={<AddIcon />} onClick={openModal}>
            {t('button.set-availability')}
          </Button>
        </div>
        <AvailabilityDataBlock availability={room.availability} />
      </section>
      {workingHours ? (
        <RoomAvailabilityModal
          open={isAvailabilityModalOpen}
          onOpenChange={setIsAvailabilityModalOpen}
          onSubmit={handleSetRoomAvailability}
          workingHours={workingHours}
          defaultValues={parseAvailabilitySlotsForForm(room.availability)}
          loading={isPending}
        />
      ) : null}
      <ConfirmModal
        header={t('header.remove-rooms_one')}
        subheader={
          <>
            <p>{t('help.want-to-remove-room', { NAME: room.name })}</p>
            <p>{t('help.this-action-cannot-be-undone')}</p>
          </>
        }
        confirmButton={
          <Button
            variant="danger"
            onClick={handleRemoveRoom}
            loading={isRemoveRoomPending}
          >
            {t('button.remove')}
          </Button>
        }
        open={isRemoveModalOpen}
        onOpenChange={setIsRemoveModalOpen}
      />
    </BasicLayout>
  )
}

const RoomDetailsHeader = ({ room }: { room: RoomDetails }) => (
  <div className={styles.headerWrapper}>
    <h1 className={styles.header}>{room?.name}</h1>
    <div className={styles.box}>
      <span className={styles.label}>Type</span>
      <Tag
        label={room.roomType}
        color={roomTypeLabelAndColorMap[room.roomType]}
        dataTestId="type-tag"
        variant="with-background"
      />
    </div>
  </div>
)

export default RoomDetailsView
